import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation
} from '@angular/core';

import { cloneDeep, defaults, isEqual } from 'lodash';

import { CardAction } from '../card-action/card-action';
import { CardBase } from '../card-base';
import { CardConfig } from './card-config';
import { CardFilter } from '../card-filter/card-filter';
import { CardFilterPosition } from '../card-filter/card-filter-position';

/**
 * Card component
 *
 * For customization, use the templates named headerTemplate and footerTemplate.
 *
 * Usage:
 * <code><pre>
 * // Individual module import
 * import { CardModule } from 'patternfly-ng/card';
 * // Or
 * import { CardModule } from 'patternfly-ng';
 *
 * &#64;NgModule({
 *   imports: [CardModule,...]
 * })
 * export class AppModule(){}
 * </pre></code>
 *
 * Optional:
 * <code><pre>
 * import { CardAction, CardConfig, CardFilter, CardFilterPosition } from 'patternfly-ng/card';
 * </pre></code>
 */

export class CardComponent extends CardBase implements DoCheck, OnInit {
  /**
   * The card config containing component properties
   */
   config: CardConfig;

  /**
   * The event emitted when an action is selected
   */
   onActionSelect = new EventEmitter();

  /**
   * The event emitted when a filter is selected
   */
   onFilterSelect = new EventEmitter();

  private defaultConfig = {
    filterPosition: CardFilterPosition.FOOTER,
    noPadding: false,
    titleBorder: true,
    topBorder: true
  } as CardConfig;
  private prevConfig: CardConfig;

  /**
   * The default constructor
   */
  constructor() {
    super();
  }

  // Initialization

  /**
   * Setup component configuration upon initialization
   */
  ngOnInit(): void {
    this.setupConfig();
  }

  /**
   * Check if the component config has changed
   */
  ngDoCheck(): void {
    // Do a deep compare on config
    if (!isEqual(this.config, this.prevConfig)) {
      this.setupConfig();
    }
  }

  /**
   * Set up default config
   */
  protected setupConfig(): void {
    if (this.config !== undefined) {
      defaults(this.config, this.defaultConfig);
    } else {
      this.config = cloneDeep(this.defaultConfig);
    }
    this.prevConfig = cloneDeep(this.config);
  }

  // Actions

  /**
   * Handle the event emitted when an action is selected
   *
   * @param {CardAction} $event The emitted CardAction object
   */
  protected handleActionSelect($event: CardAction): void {
    this.onActionSelect.emit($event);
  }

  /**
   * Handle the event emitted when a filter is selected
   *
   * @param {CardFilter} $event The emitted CardFilter object
   */
  protected handleFilterSelect($event: CardFilter): void {
    this.onFilterSelect.emit($event);
  }

  // Accessors

  /**
   * Indicates that the footer should be shown in the footer
   *
   * @returns {boolean} True if the footer should be shown in the footer
   */
  protected get showFilterInFooter(): boolean {
    return (this.config.filters && this.config.filterPosition
      && this.config.filterPosition === CardFilterPosition.FOOTER);
  }

  /**
   * Indicates that the footer should be shown in the header
   *
   * @returns {boolean} True if the footer should be shown in the header
   */
  protected get showFilterInHeader(): boolean {
    return (this.config.filters && this.config.filterPosition
      && this.config.filterPosition === CardFilterPosition.HEADER);
  }

  /**
   * Indicates that the footer should be shown
   *
   * @returns {boolean} True if the footer should be shown
   */
  get showFooter(): boolean {
    return (this.footerTemplate !== undefined || this.showFilterInFooter || this.config.action !== undefined);
  }

  /**
   * Indicates that the header should be shown
   *
   * @returns {boolean} True if the header should be shown
   */
  get showHeader(): boolean {
    return (this.headerTemplate !== undefined || this.showFilterInHeader || this.config.title !== undefined);
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  encapsulation: ViewEncapsulation.None,
  selector: 'pfng-card',
  template: `<div class="pfng-card card-pf" [ngClass]="{'card-pf-accented': config?.topBorder, 'pfng-card-no-padding': config.noPadding}"><div [ngClass]="config?.titleBorder ? 'card-pf-heading' : 'pfng-card-heading-no-bottom'" *ngIf="showHeader"><div *ngIf="headerTemplate; then showHeaderTemplate else showHeader"></div><ng-template #showHeaderTemplate><ng-template [ngTemplateOutlet]="headerTemplate"></ng-template></ng-template><ng-template #showHeader><div *ngIf="showFilterInHeader"><pfng-card-filter [filters]="config?.filters" (onFilterSelect)="handleFilterSelect($event)"></pfng-card-filter></div><h2 class="card-pf-title">{{config?.title}}</h2></ng-template></div><span *ngIf="config?.subTitle" class="card-pf-subtitle">{{config?.subTitle}}</span><div class="card-pf-body"><ng-content></ng-content></div><div class="card-pf-footer" *ngIf="showFooter"><div *ngIf="footerTemplate; then showFooterTemplate else showFooter"></div><ng-template #showFooterTemplate><ng-template [ngTemplateOutlet]="footerTemplate"></ng-template></ng-template><ng-template #showFooter><div *ngIf="showFilterInFooter"><pfng-card-filter [filters]="config?.filters" (onFilterSelect)="handleFilterSelect($event)"></pfng-card-filter></div><p><pfng-card-action [action]="config?.action" (onActionSelect)="handleActionSelect($event)"></pfng-card-action></p></ng-template></div></div>`
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'config': [{ type: Input },],
'onActionSelect': [{ type: Output, args: ['onActionSelect', ] },],
'onFilterSelect': [{ type: Output, args: ['onFilterSelect', ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
